import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import axios from 'axios';
import QRCode from 'react-qr-code';
import InactivityTimer from './InactivityTimer';

const PaymentSuccess = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [registrationStatus, setRegistrationStatus] = useState('pending'); // pending, success, failed
    const [invoiceUrl, setInvoiceUrl] = useState(null);
    const [externalMRNO, setExternalMRNO] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            const params = new URLSearchParams(location.search);
            const patientId = params.get('patientId');
            // const paymentId = params.get('paymentId');

            try {
                let patient = null;
                // let payment = null;

                // Fetch patient data if patientId is available
                if (patientId) {
                    const patientResponse = await axiosInstance.get(`/api/patient/get-patient-data/${patientId}`);
                    patient = patientResponse.data;

                    // Prepare external registration payload
                    const patientData = patient.patient; // Extract the nested patient object
                    const paymentData = patient.payment; // Extract the payment object
                    
                    const externalPayload = {
                        firstName: patientData?.firstName || '',
                        middleName: patientData?.middleName || '',
                        lastName: patientData?.lastName || '',
                        gender: patientData?.gender || '',
                        genderCode: patientData?.genderCode || '',
                        nationality: patientData?.nationality || 'Indian',
                        emailAddress: patientData?.emailAddress || '',
                        contactNumber: patientData?.contactNumber || '',
                        whatsappNumber: patientData?.whatsappNumber || '',
                        address: patientData?.address || '',
                        state: patientData?.state || '',
                        city: patientData?.city || '',
                        pinCode: patientData?.pinCode || '',
                        age: patientData?.age || null,
                        dob: patientData?.dob || null,
                        relation: patientData?.relation || null,
                        relationCode: patientData?.relationCode || null,
                        prefixName: patientData?.prefixName || null,
                        prefix: patientData?.prefix || patientData?.prefixNameText || '',
                        agree: patientData?.agree ? 1 : 0,
                        receiveCall: patientData?.receiveCall ? 1 : 0,
                        base64String: patientData?.base64String || '',
                        siteCode: `${localStorage.getItem('site_code') || 'SSCC'}`,
                        siteId: `${localStorage.getItem('site_id') || '2464'}`,
                        maritalStatus: patientData?.maritalStatus || '',
                        maritialStatusCode: patientData?.maritialStatusCode || '',
                        emergencyContactNo: patientData?.emergencyContactNo || '',
                        emergencyContactName: patientData?.emergencyContactName || '',
                        postOffice: patientData?.postOffice || '',
                        streetvillage: patientData?.streetvillage || '',
                        policeStation: patientData?.policeStation || '',
                        district: patientData?.district || '',
                        addressList: [{
                            address: patientData?.address || '',
                            countryId: patientData?.countryId || null,
                            stateId: patientData?.stateId || null,
                            districtId: patientData?.districtId || null,
                            cityId: patientData?.cityId || null,
                            stateName: patientData?.state || patientData?.stateName || '',
                            cityName: patientData?.city || patientData?.cityName || '',
                            pin: patientData?.pinCode || '',
                            postOffice: patientData?.postOffice || '',
                            village: patientData?.streetvillage || '',
                            policeStation: patientData?.policeStation || '',
                            districtName: patientData?.district || patientData?.districtName || ''
                        }],
                        payment: {
                            paymentStatus: 'success',
                            amount: (paymentData?.amount_received || paymentData?.amount_initiated || 100),
                            currency: 'INR',
                            orderId: paymentData?.razorpayOrderId || paymentData?.orderId || '',
                            paymentId: paymentData?.razorpayPaymentId || paymentData?.paymentId || '',
                        }
                    };

                    console.log('External API Payload:', JSON.stringify(externalPayload, null, 2));

                    try {
                        // Get authorization token from localStorage
                        const token = localStorage.getItem('token');
                        
                        setRegistrationStatus('processing');
                        
                        const extRes = await axios.post(
                            `${process.env.REACT_APP_KIOSK_OPS_URL}/patient/newRegistration?pay=false`,
                            externalPayload,
                            {
                                headers: {
                                    'Authorization': token ? `Bearer ${token}` : '',
                                    'Content-Type': 'application/json'
                                }
                            }
                        );

                        console.log('External registration response:', extRes.data);

                        if (extRes.data && (extRes.data.success || extRes.data.mrno)) {
                            setRegistrationStatus('success');
                            setExternalMRNO(extRes.data.mrno);
                            setInvoiceUrl(extRes.data.invoiceUrl);
                            console.log('External MRNO received:', extRes.data.mrno);
                            console.log('Invoice URL received:', extRes.data.invoiceUrl);

                            // NEW: Update local database with external HIMS data
                            try {
                                const updateResponse = await axiosInstance.put('/api/patient/update-patient-external-data', {
                                    patientId: patientId,
                                    externalPatientId: extRes.data.patientId || extRes.data.id || null,
                                    externalMRNO: extRes.data.mrno
                                });
                                console.log('Local database updated with external data:', updateResponse.data);
                            } catch (updateError) {
                                console.error('Failed to update local database with external data:', updateError);
                                // Don't change registration status, external registration was successful
                            }
                        } else {
                            setRegistrationStatus('failed');
                            console.warn('External registration completed but no MRNO received');
                        }
                    } catch (externalError) {
                        setRegistrationStatus('failed');
                        console.error('External registration failed:', externalError);
                        console.error('Error response:', externalError.response?.data);
                    }
                }

                // Fetch payment details if paymentId is available
                // if (paymentId) {
                //     const paymentResponse = await axiosInstance.get(`/api/patient/get-payment-details/${paymentId}`);
                //     payment = paymentResponse.data;
                // }

                // Set data for registration success (no appointment involved)
                setData({
                    patientName: patient ? `${patient.patient?.firstName || patient.firstName} ${patient.patient?.middleName || patient.middleName || ''} ${patient.patient?.lastName || patient.lastName || ''}`.trim() : 'N/A',
                    patientMrno: patient?.patient?.mrno || patient?.mrno || 'N/A',
                    doctorName: 'N/A', // No doctor in registration flow
                    specialty: 'Registration', // This is registration payment
                    orderId: patient?.payment?.razorpayOrderId || patient?.payment?.orderId || 'N/A',
                    paymentId: patient?.payment?.razorpayPaymentId || patient?.payment?.paymentId || 'N/A',
                    amount: patient?.payment?.amount_received ? (patient.payment.amount_received) : 'N/A', // Registration fee in rupees
                    patientId: patient?.patient?.id || patient?.id || patientId,
                });
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [location]);

    if (loading) {
        return <div className="text-center mt-10">Loading...</div>;
    }

    if (!data) {
        return <div className="text-center mt-10">No data available</div>;
    }

    // Removed consultation-related functions since this is for registration payment only
    /*
    const getConsultationRequestBody = () => {
        if (!data) return null;
        return {
            unitId: String(data.unitId),
            createdBy: '2',
            departmentId: String(data.departmentId),
            consultantId: String(data.consultantId),
            // Use externalMRNO if present, else fallback to local
            patientId: "3682778",//String(data.externalMRNO || data.patientId),
            siteId: '2468',
            serviceCenterId: '86158',
            // receiptId: String(data.receiptId || data.appointmentId),
            payment: {
                paymentId: String(data.paymentId),
                amount: String(data.amount), // Convert to rupees
                currency: 'INR',
                orderId: String(data.orderId),
                status: 'SUCCESS',
            },
        };
    };

    const handlePreviewSlip = async () => {
        const reqBody = getConsultationRequestBody();
        if (!reqBody) return;
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${process.env.REACT_APP_KIOSK_OPS_URL}/consultation/create`,
                reqBody,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data && response.data.downloadUrl) {
                // Open the downloadUrl in a new tab
                window.open(response.data.downloadUrl, '_blank');
            } else {
                alert('No download URL found in response.');
            }
        } catch (error) {
            alert('Failed to fetch consultation slip');
        }
    };

    const handlePrintSlip = async () => {
        const reqBody = getConsultationRequestBody();
        if (!reqBody) return;
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${process.env.REACT_APP_KIOSK_OPS_URL}/consultation/create`,
                reqBody,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data && response.data.downloadUrl) {
                setIframeUrl(response.data.downloadUrl);
                setShowIframeModal(true);
            } else {
                alert('No download URL found in response.');
            }
        } catch (error) {
            alert('Failed to fetch consultation slip');
        }
    };
    */

    return (
        <>
            <InactivityTimer />
            <div className="fixed inset-0 z-50 flex justify-center items-center h-full bg-black bg-opacity-50 overflow-y-auto p-2">
                <div className="relative p-8 w-full max-w-[95vw] bg-white rounded-2xl shadow-2xl mx-2">
                    <div className="text-center">
                        <svg className="w-16 h-16 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <h2 className="text-3xl font-bold text-gray-800 mb-3">Registration Successful!</h2>
                        <p className="text-gray-600 mb-6">Your registration has been completed successfully.</p>
                        {/* Two Column Layout: Patient Details Left, QR Code Right */}
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-12 mb-8">
                            {/* Left Side - Patient Details */}
                            <div className="bg-gray-50 p-8 rounded-xl">
                                <h3 className="text-xl font-bold text-gray-800 mb-4">
                                    Patient Details
                                </h3>
                                <div className="space-y-3 text-base">
                                    <p><strong>Patient Name:</strong> {data.patientName}</p>
                                    <p><strong>MR Number:</strong> {externalMRNO}</p>
                                    <p><strong>Order ID:</strong> {data.orderId}</p>
                                    <p><strong>Payment ID:</strong> {data.paymentId}</p>
                                    <p><strong>Amount Paid:</strong> ₹{data.amount}</p>
                                    <div className="mt-3 pt-2 border-t border-gray-200">
                                        <p className="mb-1"><strong>Payment Status:</strong> <span className="text-green-600">Success</span></p>
                                        <p>
                                            <strong>Registration:</strong>
                                            <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                                                registrationStatus === 'success' 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : registrationStatus === 'failed'
                                                    ? 'bg-red-100 text-red-800'
                                                    : registrationStatus === 'processing'
                                                    ? 'bg-blue-100 text-blue-800'
                                                    : 'bg-yellow-100 text-yellow-800'
                                            }`}>
                                                {registrationStatus === 'success' 
                                                    ? 'Completed' 
                                                    : registrationStatus === 'failed'
                                                    ? 'Failed'
                                                    : registrationStatus === 'processing'
                                                    ? 'Processing...'
                                                    : 'Pending'}
                                            </span>
                                        </p>
                                        {externalMRNO && (
                                            <p className="mt-1"><strong>Patient UHID:</strong> <span className="text-blue-600">{externalMRNO}</span></p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Right Side - QR Code Section */}
                            {invoiceUrl && registrationStatus === 'success' ? (
                                <div className="bg-blue-50 p-8 rounded-xl border border-blue-200">
                                    <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center justify-center">
                                        <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                                        </svg>
                                        Invoice QR Code
                                    </h3>
                                    <div className="flex flex-col items-center">
                                        <div className="bg-white p-6 rounded-xl shadow-md">
                                            <QRCode 
                                                value={invoiceUrl} 
                                                size={200}
                                                level="M"
                                                includeMargin={true}
                                            />
                                        </div>
                                        <p className="text-base text-gray-600 mt-4 text-center">Scan to download invoice</p>
                                        <button
                                            onClick={() => window.open(invoiceUrl, '_blank')}
                                            className="mt-4 px-6 py-3 bg-blue-600 text-white text-base font-medium rounded-lg hover:bg-blue-700 transition duration-200 flex items-center"
                                        >
                                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            Download
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-gray-100 p-8 rounded-xl border border-gray-200 flex flex-col items-center justify-center min-h-[320px]">
                                    <svg className="w-20 h-20 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                                    </svg>
                                    <p className="text-gray-500 text-base text-center">
                                        {registrationStatus === 'processing' ? 'QR Code will appear after registration...' : 'QR Code not available'}
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-center gap-4 mt-8">
                            <button
                                onClick={() => navigate('/')}
                                className="px-8 py-3 bg-[#0FA968] text-white font-semibold text-lg rounded-xl shadow-lg hover:bg-[#138F5E] transition duration-200"
                            >
                                Back to Home
                            </button>
                        </div>
                    </div>
                </div>
                {/* Removed preview and print modals since this is for registration only */}
            </div>
        </>
    );
};

export default PaymentSuccess;